import React, { useRef, useEffect } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;

const regionCenters = {
  Manhattan: [-73.9712, 40.7831],
  Brooklyn: [-73.9442, 40.6782],
  Queens: [-73.7949, 40.7282],
  "New Jersey": [-74.4057, 40.0583],
};

const regionBounds = {
  Manhattan: {
    north: 40.8820,
    south: 40.6763,
    east: -73.9070,
    west: -74.0479,
  },
  Brooklyn: {
    north: 40.7394,
    south: 40.5707,
    east: -73.8334,
    west: -74.0419,
  },
  Queens: {
    north: 40.8007,
    south: 40.5417,
    east: -73.7004,
    west: -73.9261,
  },
  "New Jersey": {
    north: 41.3572,
    south: 39.9526,
    east: -73.8969,
    west: -75.5636,
  },
};

function MapView({ filter, regionFilter, selectedPlace, onPlaceClick }) {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const popupRef = useRef(null);
  const markerRef = useRef(null);

  const clearPopupAndMarker = () => {
    if (popupRef.current) popupRef.current.remove();
    if (markerRef.current) markerRef.current.remove();
  };

  const isWithinBounds = (coordinates, bounds) => {
    const [lng, lat] = coordinates;
    return (
      lat <= bounds.north &&
      lat >= bounds.south &&
      lng <= bounds.east &&
      lng >= bounds.west
    );
  };

  const loadGeoJSONData = () => {
    fetch("/data/coordinate_with_tags_updated.geojson")
      .then((response) => response.json())
      .then((data) => {
        const source = map.current.getSource("places");
        if (source) {
          source.setData({
            type: "FeatureCollection",
            features: data.features || [],
          });
        }
      })
      .catch((error) => {
        console.error("Error loading GeoJSON:", error);
        alert("Failed to load map data. Please try again later.");
      });
  };

  useEffect(() => {
    if (!map.current) {
      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: process.env.REACT_APP_MAPBOX_STYLE_URL,
        center: [-73.935242, 40.73061],
        zoom: 10,
      });

      map.current.on("load", () => {
        map.current.addSource("places", {
          type: "geojson",
          data: {
            type: "FeatureCollection",
            features: [],
          },
        });

        map.current.addLayer({
          id: "places-layer",
          type: "circle",
          source: "places",
          paint: {
            "circle-radius": 8,
            "circle-color": "#007bff",
            "circle-stroke-width": 2,
            "circle-stroke-color": "#0056b3",
          },
        });

        map.current.on("click", "places-layer", (e) => {
          const clickedFeature = e.features[0];
          if (clickedFeature) {
            const coordinates = clickedFeature.geometry.coordinates;
            let properties = clickedFeature.properties["Other Properties"];
            if (typeof properties === "string") {
              try {
                properties = JSON.parse(properties);
              } catch (error) {
                console.error("Failed to parse Other Properties:", error);
                properties = {};
              }
            }

            const name = properties?.Name || "N/A";
            const phone = properties?.Phone || "N/A";
            const website = properties?.Website || "N/A";
            const address = properties?.Address || "N/A";

            clearPopupAndMarker();

            popupRef.current = new mapboxgl.Popup({ offset: 25, closeButton: false })
              .setLngLat(coordinates)
              .setHTML(`
                <div>
                  <h4>${name}</h4>
                  <p><strong>Phone:</strong> ${phone}</p>
                  <p><strong>Website:</strong> <a href="${website}" target="_blank" rel="noopener noreferrer">${website}</a></p>
                  <p><strong>Address:</strong> ${address}</p>
                </div>
              `)
              .addTo(map.current);

            const customMarker = document.createElement("div");
            customMarker.style.backgroundImage = "url('/pinpoint.webp')";
            customMarker.style.width = "30px";
            customMarker.style.height = "30px";
            customMarker.style.backgroundSize = "cover";

            markerRef.current = new mapboxgl.Marker(customMarker)
              .setLngLat(coordinates)
              .addTo(map.current);

            onPlaceClick(clickedFeature);
          }
        });

        map.current.on("mouseenter", "places-layer", () => {
          map.current.getCanvas().style.cursor = "pointer";
        });

        map.current.on("mouseleave", "places-layer", () => {
          map.current.getCanvas().style.cursor = "";
        });

        loadGeoJSONData();
      });
    }
  }, [onPlaceClick]);

  useEffect(() => {
    if (!map.current) return;
    clearPopupAndMarker();

    fetch("/data/coordinate_with_tags_updated.geojson")
      .then((response) => response.json())
      .then((data) => {
        let filteredFeatures = data.features;

        if (filter) {
          filteredFeatures = filteredFeatures.filter((feature) =>
            feature.properties.common_Tag.toLowerCase().includes(filter.toLowerCase())
          );
        }

        if (regionFilter) {
          const bounds = regionBounds[regionFilter];
          if (bounds) {
            filteredFeatures = filteredFeatures.filter((feature) =>
              isWithinBounds(feature.geometry.coordinates, bounds)
            );

            const center = regionCenters[regionFilter];
            if (center) {
              map.current.flyTo({
                center,
                zoom: 12,
              });
            }
          }
        }

        const source = map.current.getSource("places");
        if (source) {
          source.setData({
            type: "FeatureCollection",
            features: filteredFeatures,
          });
        }
      })
      .catch((error) => console.error("Error loading GeoJSON:", error));
  }, [filter, regionFilter]);

  useEffect(() => {
    if (!map.current || !selectedPlace) return;

    const { coordinates } = selectedPlace.geometry;
    let properties = selectedPlace.properties["Other Properties"];

    map.current.flyTo({
      center: coordinates,
      zoom: 14,
    });

    if (typeof properties === "string") {
      try {
        properties = JSON.parse(properties);
      } catch (error) {
        console.error("Failed to parse Other Properties:", error);
        properties = {};
      }
    }

    const name = properties?.Name || "N/A";
    const phone = properties?.Phone || "N/A";
    const website = properties?.Website || "N/A";
    const address = properties?.Address || "N/A";

    clearPopupAndMarker();

    popupRef.current = new mapboxgl.Popup({ offset: 25, closeButton: false })
      .setLngLat(coordinates)
      .setHTML(`
        <div>
          <h4>${name}</h4>
          <p><strong>Phone:</strong> ${phone}</p>
          <p><strong>Website:</strong> <a href="${website}" target="_blank" rel="noopener noreferrer">${website}</a></p>
          <p><strong>Address:</strong> ${address}</p>
        </div>
      `)
      .addTo(map.current);

    const customMarker = document.createElement("div");
    customMarker.style.backgroundImage = "url('/pinpoint.webp')";
    customMarker.style.width = "50px";
    customMarker.style.height = "50px";
    customMarker.style.backgroundSize = "cover";

    markerRef.current = new mapboxgl.Marker(customMarker)
      .setLngLat(coordinates)
      .addTo(map.current);
  }, [selectedPlace]);

  return <div ref={mapContainer} style={{ width: "100%", height: "100%" }} />;
}

export default MapView;
